import type { ButtonHTMLAttributes, ReactNode } from 'react'
import type { TaskPriority, TaskStatus, ProjectStatus, MeetingStatus, Pillar } from '../api/types'

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ')
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
  loading?: boolean
}

const BUTTON_VARIANTS = {
  primary:
    'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-800 disabled:bg-brand-300 dark:disabled:bg-brand-900',
  secondary:
    'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800',
  ghost:
    'text-slate-600 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100',
  danger:
    'border border-red-300 bg-white text-red-700 hover:bg-red-50 dark:border-red-900 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950',
} as const

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  className,
  children,
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      disabled={disabled || loading}
      className={cx(
        'inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors',
        'disabled:cursor-not-allowed disabled:opacity-70',
        size === 'sm' ? 'px-2.5 py-1.5 text-xs' : 'px-3.5 py-2 text-sm',
        BUTTON_VARIANTS[variant],
        className,
      )}
    >
      {loading && <Spinner className="h-3.5 w-3.5" />}
      {children}
    </button>
  )
}

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx('animate-spin', className ?? 'h-5 w-5')}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
      <path
        d="M12 2a10 10 0 0 1 10 10"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Status and priority
// ---------------------------------------------------------------------------

export const TASK_STATUS_LABEL: Record<TaskStatus, string> = {
  TODO: 'To do',
  IN_PROGRESS: 'In progress',
  BLOCKED: 'Blocked',
  REVIEW: 'Review',
  DONE: 'Done',
}

const TASK_STATUS_STYLE: Record<TaskStatus, string> = {
  TODO: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  IN_PROGRESS: 'bg-brand-100 text-brand-800 dark:bg-brand-900/50 dark:text-brand-200',
  BLOCKED: 'bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300',
  REVIEW: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  DONE: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
}

export function StatusPill({ status }: { status: TaskStatus }) {
  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium',
        TASK_STATUS_STYLE[status],
      )}
    >
      {TASK_STATUS_LABEL[status]}
    </span>
  )
}

export const PRIORITY_LABEL: Record<TaskPriority, string> = {
  LOW: 'Low',
  MEDIUM: 'Medium',
  HIGH: 'High',
  URGENT: 'Urgent',
}

const PRIORITY_STYLE: Record<TaskPriority, string> = {
  LOW: 'text-slate-400',
  MEDIUM: 'text-slate-500 dark:text-slate-400',
  HIGH: 'text-amber-600 dark:text-amber-400',
  URGENT: 'text-red-600 dark:text-red-400',
}

/** Urgency is shown as a small flag rather than a colour-only cue. */
export function PriorityFlag({ priority }: { priority: TaskPriority }) {
  if (priority === 'LOW' || priority === 'MEDIUM') return null
  return (
    <span
      className={cx('inline-flex items-center gap-1 text-xs font-medium', PRIORITY_STYLE[priority])}
      title={`${PRIORITY_LABEL[priority]} priority`}
    >
      <svg viewBox="0 0 16 16" className="h-3 w-3" fill="currentColor" aria-hidden="true">
        <path d="M3 1.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 .38.82L10.15 5l2.73 3.18A.5.5 0 0 1 12.5 9h-9v5.5a.5.5 0 0 1-1 0v-13Z" />
      </svg>
      {PRIORITY_LABEL[priority]}
    </span>
  )
}

export const PROJECT_STATUS_LABEL: Record<ProjectStatus, string> = {
  ACTIVE: 'Active',
  ON_HOLD: 'On hold',
  COMPLETED: 'Completed',
  ARCHIVED: 'Archived',
}

const PROJECT_STATUS_STYLE: Record<ProjectStatus, string> = {
  ACTIVE: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300',
  ON_HOLD: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  COMPLETED: 'bg-brand-100 text-brand-800 dark:bg-brand-900/50 dark:text-brand-200',
  ARCHIVED: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
}

export function ProjectStatusPill({ status }: { status: ProjectStatus }) {
  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center rounded-full px-2 py-0.5 text-xs font-medium',
        PROJECT_STATUS_STYLE[status],
      )}
    >
      {PROJECT_STATUS_LABEL[status]}
    </span>
  )
}

export const MEETING_STATUS_LABEL: Record<MeetingStatus, string> = {
  SCHEDULED: 'Scheduled',
  CANCELLED: 'Cancelled',
}

/**
 * Only worth showing once a meeting is cancelled. A scheduled meeting is the
 * normal case and a pill saying so on every row is just noise.
 */
export function MeetingStatusPill({ status }: { status: MeetingStatus }) {
  if (status === 'SCHEDULED') return null
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-400">
      {MEETING_STATUS_LABEL[status]}
    </span>
  )
}

export const PILLAR_LABEL: Record<Pillar, string> = {
  LOGISTICS: 'Logistics',
  PROJECTS: 'Projects',
  INVESTMENTS: 'Investments',
  OTHER: 'Other',
}

export function Chip({
  children,
  title,
  tone = 'neutral',
}: {
  children: ReactNode
  title?: string
  /** `accent` uses the brand's Green-teal, reserved for group/entity labelling. */
  tone?: 'neutral' | 'accent'
}) {
  return (
    <span
      title={title}
      className={cx(
        'inline-flex shrink-0 items-center rounded-md px-1.5 py-0.5 text-xs',
        tone === 'accent'
          ? // text-accent-600 is Green-teal #075550 exactly, as issued.
            'bg-accent-50 text-accent-600 dark:bg-accent-800 dark:text-accent-100'
          : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
      )}
    >
      {children}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

export type Tab<Id extends string> = {
  id: Id
  label: string
  /** Optional trailing number. Omit it and the markup is exactly as before. */
  count?: number
}

/**
 * The underlined tab strip. It was copied verbatim between Team and a project's
 * detail page and Meetings needed a third, so this is that same markup, kept
 * pixel for pixel, extracted once.
 *
 * `capitalize` is still in the class list, which is what lets a page hand its
 * own lowercase state values straight through as labels.
 *
 * These are buttons rather than role="tab". A real tablist owes the user arrow
 * key navigation and aria-controls pointing at a panel, and half of that
 * contract is worse for a screen reader than plain buttons. aria-current marks
 * the selected one without promising behaviour that is not there.
 */
export function TabStrip<Id extends string>({
  tabs,
  active,
  onChange,
  className,
}: {
  tabs: readonly Tab<Id>[]
  active: Id
  onChange: (id: Id) => void
  className?: string
}) {
  return (
    <div
      className={cx('mb-5 flex gap-1 border-b border-slate-200 dark:border-slate-800', className)}
    >
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          aria-current={active === tab.id ? 'true' : undefined}
          className={cx(
            '-mb-px border-b-2 px-3 py-2 text-sm font-medium capitalize transition-colors',
            active === tab.id
              ? 'border-brand-600 text-brand-700 dark:border-brand-400 dark:text-brand-300'
              : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
          )}
        >
          {tab.label}
          {tab.count !== undefined && (
            <span className="ml-1.5 text-xs tabular-nums text-slate-400 dark:text-slate-500">
              {tab.count}
            </span>
          )}
        </button>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Layout helpers
// ---------------------------------------------------------------------------

export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string
  subtitle?: string
  actions?: ReactNode
}) {
  return (
    <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-50">
          {title}
        </h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description?: string
  action?: ReactNode
}) {
  return (
    <div className="card flex flex-col items-center justify-center px-6 py-12 text-center">
      <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{title}</p>
      {description && (
        <p className="mt-1 max-w-md text-sm text-slate-500 dark:text-slate-400">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  )
}

export function ErrorNotice({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : 'Something went wrong'
  return (
    <div
      role="alert"
      className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/50 dark:text-red-300"
    >
      {message}
    </div>
  )
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 px-1 py-8 text-sm text-slate-500 dark:text-slate-400">
      <Spinner className="h-4 w-4" />
      {label}
    </div>
  )
}

/** A labelled stat, used across the admin overview and project headers. */
export function Stat({
  label,
  value,
  tone = 'default',
}: {
  label: string
  value: number | string
  tone?: 'default' | 'warn' | 'danger' | 'good'
}) {
  const toneClass = {
    default: 'text-slate-900 dark:text-slate-50',
    warn: 'text-amber-600 dark:text-amber-400',
    danger: 'text-red-600 dark:text-red-400',
    good: 'text-emerald-600 dark:text-emerald-400',
  }[tone]

  return (
    <div className="card px-4 py-3">
      <p className="text-xs font-medium tracking-wide text-slate-500 uppercase dark:text-slate-400">
        {label}
      </p>
      <p className={cx('mt-1 text-2xl font-semibold tabular-nums', toneClass)}>{value}</p>
    </div>
  )
}
